import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CalendarDays, MapPin } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LastGameRecap() {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecap = async () => {
      try {
        const response = await axios.get(`${API}/games/last-recap`);
        setRecap(response.data.recap);
      } catch (e) {
        console.error("Error fetching last game recap:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchRecap();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!recap) {
    return (
      <div className="bg-astros-surface border border-astros-border p-6 text-center text-astros-muted">
        No recent game data available
      </div>
    );
  }

  const isWin = recap.result === "W";

  return (
    <section data-testid="last-game-recap">
      <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white mb-4">
        LAST GAME RECAP
      </h2>

      <div className="bg-astros-surface border border-astros-border">
        {/* Header: Score + Result */}
        <div className={`p-4 border-b border-astros-border ${isWin ? "bg-astros-success/10" : "bg-astros-danger/10"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className={`font-bebas text-xl px-3 py-1 ${isWin ? "bg-astros-success text-white" : "bg-astros-danger text-white"}`} data-testid="game-result-badge">
                {recap.result}
              </span>
              <div>
                <p className="font-bebas text-xl text-white" data-testid="game-matchup">
                  {recap.is_astros_home ? `vs ${recap.opponent}` : `@ ${recap.opponent}`}
                </p>
                <div className="flex items-center gap-3 text-xs text-astros-muted">
                  <span className="flex items-center gap-1">
                    <CalendarDays size={12} />
                    {recap.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin size={12} />
                    {recap.venue}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-right" data-testid="game-final-score">
              <p className="font-bebas text-4xl text-white">
                <span className={isWin ? "text-astros-success" : "text-astros-danger"}>{recap.astros_score}</span>
                <span className="text-astros-muted mx-2">-</span>
                <span>{recap.opponent_score}</span>
              </p>
              <p className="text-xs text-astros-muted uppercase">Final</p>
            </div>
          </div>
        </div>

        {/* Line Score */}
        <div className="p-4 border-b border-astros-border overflow-x-auto" data-testid="line-score">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-astros-muted">
                <th className="text-left font-medium py-1 pr-4 min-w-[120px]">Team</th>
                {recap.innings?.map(inn => (
                  <th key={inn.num} className="text-center font-medium px-2 py-1 min-w-[28px]">{inn.num}</th>
                ))}
                <th className="text-center font-bold px-3 py-1 border-l border-astros-border">R</th>
                <th className="text-center font-bold px-3 py-1">H</th>
                <th className="text-center font-bold px-3 py-1">E</th>
              </tr>
            </thead>
            <tbody>
              <tr className={!recap.is_astros_home ? "text-astros-orange font-semibold" : "text-white"}>
                <td className="py-1 pr-4 text-xs">{recap.away_team}</td>
                {recap.innings?.map(inn => (
                  <td key={inn.num} className="text-center px-2 py-1">{inn.away}</td>
                ))}
                <td className="text-center px-3 py-1 border-l border-astros-border font-bold">
                  {recap.is_astros_home ? recap.opponent_score : recap.astros_score}
                </td>
                <td className="text-center px-3 py-1 font-bold">
                  {recap.is_astros_home ? recap.opponent_hits : recap.astros_hits}
                </td>
                <td className="text-center px-3 py-1 font-bold">
                  {recap.is_astros_home ? recap.opponent_errors : recap.astros_errors}
                </td>
              </tr>
              <tr className={recap.is_astros_home ? "text-astros-orange font-semibold" : "text-white"}>
                <td className="py-1 pr-4 text-xs">{recap.home_team}</td>
                {recap.innings?.map(inn => (
                  <td key={inn.num} className="text-center px-2 py-1">{inn.home}</td>
                ))}
                <td className="text-center px-3 py-1 border-l border-astros-border font-bold">
                  {recap.is_astros_home ? recap.astros_score : recap.opponent_score}
                </td>
                <td className="text-center px-3 py-1 font-bold">
                  {recap.is_astros_home ? recap.astros_hits : recap.opponent_hits}
                </td>
                <td className="text-center px-3 py-1 font-bold">
                  {recap.is_astros_home ? recap.astros_errors : recap.opponent_errors}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Pitching Decisions */}
        <div className="p-4 border-b border-astros-border">
          <div className="flex flex-wrap gap-6 text-sm">
            {recap.winning_pitcher && (
              <div>
                <span className="text-astros-muted text-xs uppercase tracking-wider">W: </span>
                <span className="text-astros-success font-semibold">{recap.winning_pitcher}</span>
              </div>
            )}
            {recap.losing_pitcher && (
              <div>
                <span className="text-astros-muted text-xs uppercase tracking-wider">L: </span>
                <span className="text-astros-danger font-semibold">{recap.losing_pitcher}</span>
              </div>
            )}
            {recap.save_pitcher && (
              <div>
                <span className="text-astros-muted text-xs uppercase tracking-wider">SV: </span>
                <span className="text-white font-semibold">{recap.save_pitcher}</span>
              </div>
            )}
          </div>
        </div>

        {/* Box Score: Batters + Pitchers */}
        <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-astros-border">
          {/* Astros Batters */}
          <div className="p-4" data-testid="batters-box">
            <h4 className="font-bebas text-base text-astros-orange tracking-wider mb-3">ASTROS BATTING</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-astros-muted border-b border-astros-border">
                    <th className="text-left py-1 pr-2 font-medium">Player</th>
                    <th className="text-center px-1 py-1 font-medium">AB</th>
                    <th className="text-center px-1 py-1 font-medium">H</th>
                    <th className="text-center px-1 py-1 font-medium">HR</th>
                    <th className="text-center px-1 py-1 font-medium">RBI</th>
                    <th className="text-center px-1 py-1 font-medium">BB</th>
                    <th className="text-center px-1 py-1 font-medium">K</th>
                  </tr>
                </thead>
                <tbody>
                  {recap.batters?.map((b, i) => (
                    <motion.tr
                      key={i}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      className="text-white border-b border-astros-border/30 hover:bg-astros-border/20"
                    >
                      <td className="py-1.5 pr-2 font-medium truncate max-w-[130px]">{b.name}</td>
                      <td className="text-center px-1 py-1.5">{b.ab}</td>
                      <td className={`text-center px-1 py-1.5 ${b.hits > 0 ? "text-astros-success font-bold" : ""}`}>{b.hits}</td>
                      <td className={`text-center px-1 py-1.5 ${b.hr > 0 ? "text-astros-orange font-bold" : ""}`}>{b.hr}</td>
                      <td className={`text-center px-1 py-1.5 ${b.rbi > 0 ? "font-bold" : ""}`}>{b.rbi}</td>
                      <td className="text-center px-1 py-1.5">{b.bb}</td>
                      <td className="text-center px-1 py-1.5">{b.k}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Astros Pitchers */}
          <div className="p-4" data-testid="pitchers-box">
            <h4 className="font-bebas text-base text-astros-orange tracking-wider mb-3">ASTROS PITCHING</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-astros-muted border-b border-astros-border">
                    <th className="text-left py-1 pr-2 font-medium">Pitcher</th>
                    <th className="text-center px-1 py-1 font-medium">IP</th>
                    <th className="text-center px-1 py-1 font-medium">H</th>
                    <th className="text-center px-1 py-1 font-medium">ER</th>
                    <th className="text-center px-1 py-1 font-medium">K</th>
                    <th className="text-center px-1 py-1 font-medium">BB</th>
                  </tr>
                </thead>
                <tbody>
                  {recap.pitchers?.map((p, i) => (
                    <motion.tr
                      key={i}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      className="text-white border-b border-astros-border/30 hover:bg-astros-border/20"
                    >
                      <td className="py-1.5 pr-2 font-medium truncate max-w-[130px]">{p.name}</td>
                      <td className="text-center px-1 py-1.5">{p.ip}</td>
                      <td className="text-center px-1 py-1.5">{p.hits}</td>
                      <td className={`text-center px-1 py-1.5 ${p.er > 0 ? "text-astros-danger" : "text-astros-success"}`}>{p.er}</td>
                      <td className={`text-center px-1 py-1.5 ${p.k > 0 ? "text-astros-success font-bold" : ""}`}>{p.k}</td>
                      <td className="text-center px-1 py-1.5">{p.bb}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
